import { useEffect, useRef, useState } from "react";
import { Smartphone, X } from "lucide-react";

const PLUM = "#4B1F4F";
const CREAM = "#FAF7F5";
const MUTED = "#9B7F9E";

const DISMISS_KEY = "knowmeno_pwa_dismiss";
const DISMISS_WINDOW_MS = 60 * 1000;
const MIN_DELAY_MS = 2000;
const TIME_TRIGGER_MS = 10000;
const SCROLL_TRIGGER = 0.3;
const MOBILE_BREAKPOINT = 768;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function recentlyDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { timestamp?: number };
    if (!parsed?.timestamp) return false;
    return Date.now() - parsed.timestamp < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

function rememberDismiss() {
  try {
    window.localStorage.setItem(
      DISMISS_KEY,
      JSON.stringify({ timestamp: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

export function LandingPwaPrompt() {
  const [open, setOpen] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const bipRef = useRef<BeforeInstallPromptEvent | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const armedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    console.log("[PWA sheet] mobile detected:", isMobile, "innerWidth:", window.innerWidth);
    if (!isMobile) return;

    // already installed?
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    console.log("[PWA sheet] already installed (standalone):", isStandalone);
    if (isStandalone) return;

    const dismissRaw = window.localStorage.getItem(DISMISS_KEY);
    const dismissed = recentlyDismissed();
    console.log("[PWA sheet] dismissal state:", dismissRaw, "suppress:", dismissed);
    if (dismissed) return;

    const ios =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !(window as unknown as { MSStream?: unknown }).MSStream;
    console.log("[PWA sheet] iOS:", ios);

    let canTrigger = false;

    const tryOpen = (reason: string) => {
      console.log("[PWA sheet] tryOpen reason:", reason, "canTrigger:", canTrigger);
      if (!canTrigger) return;
      if (recentlyDismissed()) return;
      setOpen(true);
    };

    const minDelay = window.setTimeout(() => {
      canTrigger = true;
    }, MIN_DELAY_MS);

    const timeTrigger = window.setTimeout(() => {
      console.log("[PWA sheet] 10s timer fired");
      armedRef.current = true;
      tryOpen("timer");
    }, TIME_TRIGGER_MS);

    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (h <= 0) return;
      const ratio = window.scrollY / h;
      if (ratio >= SCROLL_TRIGGER) {
        console.log("[PWA sheet] 30% scroll triggered, ratio:", ratio.toFixed(2));
        armedRef.current = true;
        tryOpen("scroll");
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const onBip = (e: Event) => {
      e.preventDefault();
      bipRef.current = e as BeforeInstallPromptEvent;
      console.log("[PWA sheet] beforeinstallprompt captured");
      if (armedRef.current) tryOpen("bip");
    };
    window.addEventListener("beforeinstallprompt", onBip);

    const onInstalled = () => {
      rememberDismiss();
      setOpen(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    if (ios) {
      setIsIOS(true);
    }

    return () => {
      window.clearTimeout(minDelay);
      window.clearTimeout(timeTrigger);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "Tab" && sheetRef.current) {
        const focusables = sheetRef.current.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    // focus first interactive
    const t = window.setTimeout(() => {
      sheetRef.current?.querySelector<HTMLElement>("button")?.focus();
    }, 50);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    rememberDismiss();
    setOpen(false);
    // Re-show in 60s if the user still hasn't installed.
    window.setTimeout(() => {
      const isStandalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        (navigator as unknown as { standalone?: boolean }).standalone === true;
      if (isStandalone) return;
      setShowIOSInstructions(false);
      setOpen(true);
    }, DISMISS_WINDOW_MS);
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    const bip = bipRef.current;
    if (!bip) {
      // Android Chrome without beforeinstallprompt (e.g. no service worker):
      // show manual install instructions instead of silently closing.
      setShowIOSInstructions(true);
      return;
    }
    try {
      await bip.prompt();
      const choice = await bip.userChoice;
      rememberDismiss();
      setOpen(false);
      if (choice.outcome !== "accepted") {
        // already remembered above
      }
    } catch {
      close();
    } finally {
      bipRef.current = null;
    }
  };

  console.log("[PWA sheet] render check — open:", open);
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Install KnowMeno"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(20, 8, 18, 0.38)",
        animation: "kmFade 200ms ease-out",
      }}
    >
      <style>{`
        @keyframes kmFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes kmSlide { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          background: CREAM,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: "28px 24px 24px",
          boxShadow: "0 -8px 24px rgba(75, 31, 79, 0.12)",
          fontFamily: "system-ui, -apple-system, sans-serif",
          position: "relative",
          animation: "kmSlide 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <button
          onClick={close}
          aria-label="Close install prompt"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            padding: 8,
            cursor: "pointer",
            color: PLUM,
            lineHeight: 0,
          }}
        >
          <X size={22} />
        </button>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <Smartphone size={32} color={PLUM} strokeWidth={1.75} />
        </div>

        <h2
          style={{
            margin: 0,
            textAlign: "center",
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 26,
            lineHeight: 1.2,
            color: PLUM,
            fontWeight: 500,
            letterSpacing: "-0.01em",
          }}
        >
          Install KnowMeno on your phone
        </h2>

        <p
          style={{
            margin: "10px auto 20px",
            maxWidth: 380,
            textAlign: "center",
            color: PLUM,
            opacity: 0.85,
            fontSize: 15,
            lineHeight: 1.5,
          }}
        >
          One tap. Add KnowMeno to your home screen and track in seconds.
        </p>

        {isIOS && showIOSInstructions ? (
          <>
            <ol
              style={{
                background: "#fff",
                border: `1px solid rgba(75,31,79,0.08)`,
                borderRadius: 14,
                padding: "14px 16px 14px 32px",
                margin: "0 0 14px",
                color: PLUM,
                fontSize: 14.5,
                lineHeight: 1.6,
              }}
            >
              <li>Tap the <strong>Share</strong> button at the bottom of your screen.</li>
              <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
              <li>Tap <strong>Add</strong> to install.</li>
            </ol>
            <button
              onClick={close}
              style={primaryBtnStyle}
            >
              Got it
            </button>
          </>
        ) : (
          <button onClick={handleInstall} style={primaryBtnStyle}>
            {isIOS ? "How to install" : "Install now"}
          </button>
        )}

        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button
            onClick={close}
            style={{
              background: "transparent",
              border: "none",
              color: MUTED,
              fontSize: 13.5,
              textDecoration: "underline",
              cursor: "pointer",
              padding: 6,
            }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  background: PLUM,
  color: "#fff",
  border: "none",
  borderRadius: 999,
  padding: "14px 20px",
  fontSize: 15.5,
  fontWeight: 600,
  cursor: "pointer",
  letterSpacing: "0.01em",
};

export default LandingPwaPrompt;
